import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const ManageServicePages = () => {
  const navigate = useNavigate();
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const toastShownRef = useRef(false);

  // 🔎 Filters
  const [titleFilter, setTitleFilter] = useState("");
  const [headingFilter, setHeadingFilter] = useState("");
  const [seoFilter, setSeoFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // 📦 Fetch service pages from API
  const fetchServicePages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}service-pages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Service Pages Response:", response);
      if (!toastShownRef.current) {
        showToast.success("Service Pages fetched successfully");
        toastShownRef.current = true;
      }

      if (Array.isArray(response.data)) {
        setCustomPages(response.data);
      } else {
        showToast("Invalid data format", "error");
      }
    } catch (error) {
      console.error("Error fetching service pages:", error);
      showToast.error("Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicePages();
  }, []);

  // 🔎 Apply filters
  const filteredPages = useMemo(() => {
    return customPages.filter((page) => {
      if (
        titleFilter &&
        !(page.title || "").toLowerCase().includes(titleFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        headingFilter &&
        !(page.heading || "")
          .toLowerCase()
          .includes(headingFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        seoFilter &&
        !(page.seo_title || "").toLowerCase().includes(seoFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [customPages, titleFilter, headingFilter, seoFilter]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredPages.length);
    setPageCount(Math.ceil(filteredPages.length / limit));
    setPage(1);
  }, [filteredPages, limit]);

  useEffect(() => {
    if (!loading) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [titleFilter, headingFilter, seoFilter]);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPages = filteredPages.slice(start - 1, end);

  // 🧹 Handle delete
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(`${API_URL}service-pages/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setCustomPages((prevPages) =>
            prevPages.filter((page) => page.id !== id)
          );
          // showToast.success("Service page deleted successfully", "success");
        } catch (error) {
          console.error("Error deleting service page:", error);
          showToast.error("Failed to delete page", "error");
        }
      }
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTitleFilter("");
      setHeadingFilter("");
      setSeoFilter("");
    };
    window.addEventListener('clearServicePageFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearServicePageFilterEvent', handleClearFilters);
    };
  }, []);

  const handleEdit = (id) => {
    navigate(`/Seo/ServicePages/edit/${id}`);
  };

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <h4 className="panel-title mb-0">Manage Service Pages</h4>

            <div className="d-flex align-items-center">
              <PermissionWrapper module="extra_pages" action="add">
                <Link
                  className="btn btn-default btn-sm"
                  to={"/Seo/ServicePages/add"}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Page
                </Link>
              </PermissionWrapper>

              {/* 📑 Pagination Controls */}
              {customPages.length > 0 && (
                <div className="d-inline-block ms-3">
                  <span style={{ fontSize: "0.8rem" }} className="me-2">
                    {start} to {end} of {total}
                  </span>
                  <button
                    className="btn btn-default btn-sm"
                    title="Previous"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-default btn-sm"
                    title="Next"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === pageCount}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading || customPages.length > 0 ? (
            <div className="table-responsive user-table-container">
              <style>
                {`
                  .user-table-container {
                    position: relative;
                    max-height: 76vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: -1px;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }

                  .skeleton-box {
                    height: 15px;
                    background: #f2f2f2;
                    background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%);
                    background-size: 200% 100%;
                    animation: skeleton-loading 1.5s infinite;
                    border-radius: 4px;
                    width: 100%;
                  }

                  @keyframes skeleton-loading {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
               `}
              </style>
              <table className="table table-condensed table-bordered bg-white">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Title</th>
                    <th>Heading</th>
                    <th>SEO Title</th>
                    <th>SEO Keyword</th>
                    <th>Link</th>
                    <th>Action</th>
                  </tr>
                  {/* 🔎 Search Filters */}
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search title..."
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search heading..."
                        value={headingFilter}
                        onChange={(e) => setHeadingFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search SEO title..."
                        value={seoFilter}
                        onChange={(e) => setSeoFilter(e.target.value)}
                      />
                    </th>
                    <th></th>
                    <th></th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setTitleFilter("");
                          setHeadingFilter("");
                          setSeoFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || isFiltering ? (
                    Array.from({ length: 15 }).map((_, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" style={{ width: "70px" }} />
                        </td>
                        <td>
                          <div className="skeleton-box" style={{ width: "60px" }} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedPages.length > 0 ? (
                    paginatedPages.map((page, index) => (
                      <tr key={page.id}>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {start + index}
                        </td>
                        <td>{page.title}</td>
                        <td>{page.heading}</td>
                        <td>{page.seo_title}</td>
                        <td>{page.seo_keywords}</td>
                        <td>
                          <a
                            href={`https://streetgains.in/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-xs btn-primary"
                          >
                            Click Me
                          </a>
                        </td>
                        <td>
                          <PermissionWrapper
                            module="extra_pages"
                            action="edit"
                          >
                            <button
                              className="btn btn-warning btn-xs mr-2"
                              onClick={() => handleEdit(page.id)}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>
                          <PermissionWrapper
                            module="extra_pages"
                            action="delete"
                          >
                            <button
                              className="btn btn-danger btn-xs ms-2"
                              onClick={() => handleDelete(page.id)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No pages match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div>No records found.</div>
          )}
        </div>
      </div>

      {/* Scroll Top Button */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ManageServicePages;
